import {helpApi} from "../api";
import {
  getHelpCacheScope,
  readHelpCache,
  readHelpCacheStale,
  writeHelpCache,
} from "../lib/help-cache";
import type {HelpBundle, HelpField, HelpFieldsResponse, HelpIndex} from "../types";

class HelpService {
  async getIndex(userId: number): Promise<HelpIndex> {
    const scope = getHelpCacheScope(userId, "index");
    const cached = readHelpCache<HelpIndex>(userId, scope);
    if (cached) return cached;

    try {
      const {data} = await helpApi.getIndex();
      if (!data.success) {
        throw new Error(data.message || "Failed to load help index");
      }
      writeHelpCache(userId, scope, data.data);
      return data.data;
    } catch {
      const stale = readHelpCacheStale<HelpIndex>(userId, scope);
      if (stale) return stale;
      throw new Error("Failed to load help index");
    }
  }

  async getDomain(userId: number, domain: string): Promise<HelpBundle> {
    const scope = getHelpCacheScope(userId, `bundle:${domain}`);
    const cached = readHelpCache<HelpBundle>(userId, scope);
    if (cached) return cached;

    try {
      const {data} = await helpApi.getDomain(domain);
      if (!data.success) {
        throw new Error(data.message || "Failed to load help bundle");
      }
      writeHelpCache(userId, scope, data.data);
      return data.data;
    } catch {
      const stale = readHelpCacheStale<HelpBundle>(userId, scope);
      if (stale) return stale;
      throw new Error("Failed to load help bundle");
    }
  }

  async getFieldsByScreen(
    userId: number,
    domain: string,
    screen: string,
  ): Promise<HelpFieldsResponse> {
    const scope = getHelpCacheScope(userId, `fields:${domain}:${screen}`);
    const cached = readHelpCache<HelpFieldsResponse>(userId, scope);
    if (cached) return cached;

    try {
      const {data} = await helpApi.getFieldsByScreen(domain, screen);
      if (!data.success) {
        throw new Error(data.message || "Failed to load help fields");
      }
      writeHelpCache(userId, scope, data.data);
      return data.data;
    } catch {
      const stale = readHelpCacheStale<HelpFieldsResponse>(userId, scope);
      if (stale) return stale;
      throw new Error("Failed to load help fields");
    }
  }

  fieldsToMap(fields: HelpField[]) {
    return new Map(fields.map((field) => [field.key, field]));
  }
}

export const helpService = new HelpService();
